import { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink, Building2, Camera, CheckCircle2, Facebook, ImagePlus, Instagram, Link2, LoaderCircle, Lock, MessageCircle, Pencil, ThumbsUp, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { uploadFile } from "../lib/upload";

type SocialAccount = {
  id?: string;
  name?: string;
  display_name?: string;
  username?: string;
  platform?: string;
  profile_picture_url?: string;
  picture_url?: string;
  scopes?: string[];
  error?: unknown;
  external_id?: string | null;
  url?: string | null;
};

type ManagedBranding = { page_name: string; description: string; logo_url: string | null; banner_url: string | null };
type PickedImage = { id: string; name: string; preview: string };
type ManagedStep = "closed" | "details" | "preview" | "done";

function accountName(account: SocialAccount) {
  return account.name || account.display_name || "Meta asset";
}

function accountStatus(account: SocialAccount) {
  if (account.error) return "Needs attention";
  if (Object.prototype.hasOwnProperty.call(account, "external_id") && !account.external_id) return "Being prepared";
  return account.scopes?.includes("advertise") ? "Ready" : "Connected";
}

function authorizeUrl(payload: Record<string, unknown>) {
  const value = payload.authorize_url || payload.url || payload.redirect_url;
  if (typeof value !== "string") return "";
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.href : "";
  } catch {
    return "";
  }
}

// The managed Facebook Page flow is broken on the ad network's side right
// now; flip this back on once page creation works again.
const MANAGED_PAGE_ENABLED = true;

export function MetaAssetsView({ onChanged }: { onChanged: () => void }) {
  const [items, setItems] = useState<SocialAccount[] | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [step, setStep] = useState<ManagedStep>("closed");
  const [managedAttempted, setManagedAttempted] = useState(false);
  const [pageName, setPageName] = useState("");
  const [description, setDescription] = useState("");
  const [logo, setLogo] = useState<PickedImage | null>(null);
  const [banner, setBanner] = useState<PickedImage | null>(null);
  const [progress, setProgress] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorNotice, setEditorNotice] = useState("");
  const [editorBranding, setEditorBranding] = useState<ManagedBranding | null>(null);
  const [editorDescription, setEditorDescription] = useState("");
  const [editorLogo, setEditorLogo] = useState<PickedImage | null>(null);
  const [editorBanner, setEditorBanner] = useState<PickedImage | null>(null);
  const watchingCreation = useRef(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const result = await api<{ data?: SocialAccount[] }>("/api/ads/social-accounts");
      setItems(result.data || []);
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Meta assets could not be loaded.");
    }
  }, [onChanged]);

  useEffect(() => { void load(); }, [load]);

  const managedPage = items?.find((account) => account.platform === "facebook" && account.scopes?.includes("whop_owned")) || null;
  const pendingManaged = Boolean(managedPage && !managedPage.external_id);
  const liveManaged = Boolean(managedPage && managedPage.external_id);

  // While the Page is being provisioned on Facebook, keep re-checking and walk
  // the progress bar toward (but never past) the finish until it lands.
  useEffect(() => {
    if (!pendingManaged) return;
    watchingCreation.current = true;
    queueMicrotask(() => setProgress((value) => Math.max(value, 8)));
    const ticker = window.setInterval(() => {
      setProgress((value) => Math.min(92, value + Math.max(0.4, (92 - value) * 0.05)));
    }, 900);
    const poll = window.setInterval(() => { void load(); }, 5_000);
    return () => { window.clearInterval(ticker); window.clearInterval(poll); };
  }, [pendingManaged, load]);

  useEffect(() => {
    if (pendingManaged || !liveManaged || !watchingCreation.current) return;
    watchingCreation.current = false;
    queueMicrotask(() => {
      setProgress(100);
      setStep("done");
    });
  }, [pendingManaged, liveManaged]);

  const openManagedFlow = async () => {
    setError("");
    setManagedAttempted(false);
    if (step !== "closed") { setStep("closed"); return; }
    setStep("details");
    try {
      const branding = await api<ManagedBranding>("/api/ads/social-accounts/managed/branding");
      setPageName(branding.page_name);
      if (branding.description && !description) setDescription(branding.description);
    } catch {
      setPageName("");
    }
  };

  const connect = async () => {
    setBusy("connect");
    setError("");
    try {
      const result = await api<Record<string, unknown>>("/api/ads/social-accounts/connect", {
        method: "POST",
        body: JSON.stringify({ redirect_url: `${window.location.origin}/app/settings/identity` }),
      });
      const url = authorizeUrl(result);
      if (!url) throw new Error("Meta did not return a connection page. Try again.");
      window.location.assign(url);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Meta assets could not be connected.");
      setBusy("");
    }
  };

  const pickImage = async (kind: "logo" | "banner" | "editor-logo" | "editor-banner", file: File) => {
    setBusy(kind);
    setError("");
    try {
      const result = await uploadFile(file);
      const next = { id: String(result.id), name: file.name, preview: URL.createObjectURL(file) };
      if (kind === "logo") setLogo(next);
      else if (kind === "banner") setBanner(next);
      else if (kind === "editor-logo") setEditorLogo(next);
      else setEditorBanner(next);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The image could not be uploaded.");
    } finally {
      setBusy("");
    }
  };

  const toPreview = () => {
    setManagedAttempted(true);
    if (!description.trim() || !logo || !banner) {
      setError("Complete all required fields before previewing the Facebook Page.");
      return;
    }
    setError("");
    setStep("preview");
  };

  const createManaged = async () => {
    if (!description.trim() || !logo || !banner) return;
    setBusy("managed");
    setError("");
    try {
      await api("/api/ads/social-accounts/managed", {
        method: "POST",
        body: JSON.stringify({ description: description.trim(), logo_file_id: logo.id, banner_file_id: banner.id }),
      });
      setStep("closed");
      setManagedAttempted(false);
      setProgress(8);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Managed Meta assets could not be created.");
    } finally {
      setBusy("");
    }
  };

  const openEditor = async () => {
    setEditorNotice("");
    setError("");
    if (editorOpen) { setEditorOpen(false); return; }
    setEditorOpen(true);
    try {
      const branding = await api<ManagedBranding>("/api/ads/social-accounts/managed/branding");
      setEditorBranding(branding);
      setEditorDescription(branding.description);
      setEditorLogo(null);
      setEditorBanner(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The Page branding could not be loaded.");
      setEditorOpen(false);
    }
  };

  const saveEditor = async () => {
    const body: Record<string, string> = {};
    if (editorDescription.trim() && editorDescription.trim() !== (editorBranding?.description || "")) body.description = editorDescription.trim();
    if (editorLogo) body.logo_file_id = editorLogo.id;
    if (editorBanner) body.banner_file_id = editorBanner.id;
    if (Object.keys(body).length === 0) { setEditorNotice("Nothing changed yet."); return; }
    setBusy("editor");
    setError("");
    setEditorNotice("");
    try {
      await api("/api/ads/social-accounts/managed/branding", { method: "PATCH", body: JSON.stringify(body) });
      setEditorBranding((current) => current ? {
        ...current,
        description: body.description || current.description,
        logo_url: editorLogo?.preview || current.logo_url,
        banner_url: editorBanner?.preview || current.banner_url,
      } : current);
      setEditorLogo(null);
      setEditorBanner(null);
      setEditorNotice("Saved. Facebook picks up branding changes within a few minutes.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The Page branding could not be updated.");
    } finally {
      setBusy("");
    }
  };

  const disconnect = async (account: SocialAccount) => {
    if (!account.id || !window.confirm(`Disconnect ${accountName(account)}?`)) return;
    setBusy(account.id);
    setError("");
    try {
      await api(`/api/ads/social-accounts/${encodeURIComponent(account.id)}`, { method: "DELETE" });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The Meta asset could not be disconnected.");
    } finally {
      setBusy("");
    }
  };

  const creationCaption = progress < 25 ? "Reserving your Page…" : progress < 60 ? "Creating the Page on Facebook…" : progress < 100 ? "Applying your branding…" : "Your Page is ready.";

  return <div className="assets-workspace">
    <section className="asset-actions">
      <article><span className="asset-action-icon"><Link2 /></span><div><strong>Connect your Facebook or Instagram</strong><p>Choose the Facebook Page or Instagram account you already use.</p></div><button className="button button--primary" type="button" onClick={connect} disabled={Boolean(busy)}>{busy === "connect" ? "Opening Meta…" : "Connect existing account"}</button></article>
      {MANAGED_PAGE_ENABLED && !managedPage && <article><span className="asset-action-icon asset-action-icon--warm"><Building2 /></span><div><strong>Create a Facebook Page powered by AdRoyalties</strong><p>Share your brand details and AdRoyalties will create the Facebook Page your campaigns run from.</p></div><button className="button button--secondary" type="button" onClick={() => void openManagedFlow()}>Create Facebook Page</button></article>}
      {MANAGED_PAGE_ENABLED && liveManaged && <article><span className="asset-action-icon asset-action-icon--warm"><Pencil /></span><div><strong>Edit your Facebook Page</strong><p>Update the profile picture, banner, and description. The Page name stays locked to your ad account name.</p></div><button className="button button--secondary" type="button" onClick={() => void openEditor()}>{editorOpen ? "Close editor" : "Edit Page branding"}</button></article>}
    </section>

    {error && <div className="form-alert" role="alert">{error}</div>}

    {MANAGED_PAGE_ENABLED && step === "details" && !managedPage && <form className="managed-assets-form" noValidate onSubmit={(event) => { event.preventDefault(); toPreview(); }}>
      <div className="managed-assets-form__intro"><span className="eyebrow">POWERED BY ADROYALTIES</span><h2>Create your Facebook Page</h2><p>We’ll use these details to create the Page your campaigns run from.</p></div>
      <div className="managed-assets-form__fields">
        <label className="managed-name-field" htmlFor="managed-page-name"><span>Page name <Lock aria-hidden="true" /></span><input id="managed-page-name" value={pageName || "Loading…"} readOnly aria-describedby="managed-page-name-note" /></label>
        <div className="managed-name-warning" id="managed-page-name-note" role="note"><Lock aria-hidden="true" /><p><strong>The Page name can’t be changed after the Page is created.</strong> It always matches your ad account name. To use a different name, rename your ad account in Settings → General first, then create the Page.</p></div>
        <p className="required-fields-note"><span aria-hidden="true">*</span> Required fields</p>
        <label className={managedAttempted && !description.trim() ? "has-error" : ""} htmlFor="managed-business-description"><span>Business description <em aria-hidden="true">*</em></span><textarea id="managed-business-description" maxLength={500} value={description} onChange={(event) => { setDescription(event.target.value); setError(""); }} placeholder="What your business does and who it serves" required aria-invalid={managedAttempted && !description.trim()} aria-describedby={managedAttempted && !description.trim() ? "managed-description-error" : undefined} />{managedAttempted && !description.trim() && <small className="field-error" id="managed-description-error">Required</small>}</label>
        <div className="asset-upload-grid">
          <ImageField label="Profile picture (square logo)" value={logo?.name} busy={busy === "logo"} required invalid={managedAttempted && !logo} errorId="managed-logo-error" onFile={(file) => { setError(""); void pickImage("logo", file); }} />
          <ImageField label="Banner (wide cover photo)" value={banner?.name} busy={busy === "banner"} required invalid={managedAttempted && !banner} errorId="managed-banner-error" onFile={(file) => { setError(""); void pickImage("banner", file); }} />
        </div>
        <div className="managed-assets-form__actions"><button className="button button--primary" type="submit" disabled={Boolean(busy)}>Preview your Page</button><button className="button button--secondary" type="button" onClick={() => { setStep("closed"); setManagedAttempted(false); setError(""); }}>Cancel</button></div>
      </div>
    </form>}

    {MANAGED_PAGE_ENABLED && step === "preview" && !managedPage && <section className="managed-assets-form">
      <div className="managed-assets-form__intro"><span className="eyebrow">PREVIEW</span><h2>How your Page will look on Facebook</h2><p>This is a preview. Confirm below and AdRoyalties creates the real Page.</p></div>
      <div className="managed-assets-form__fields">
        <FacebookPreview name={pageName || "Your Page"} about={description.trim()} logoUrl={logo?.preview || null} bannerUrl={banner?.preview || null} />
        <div className="managed-name-warning" role="note"><Lock aria-hidden="true" /><p><strong>Last chance to change the name.</strong> After creation, “{pageName}” is permanent: Facebook doesn’t allow the Page to be renamed. The profile picture, banner, and description stay editable.</p></div>
        <div className="managed-assets-form__actions"><button className="button button--primary" type="button" disabled={Boolean(busy)} onClick={() => void createManaged()}>{busy === "managed" ? "Starting…" : "Confirm and create Page"}</button><button className="button button--secondary" type="button" disabled={Boolean(busy)} onClick={() => setStep("details")}>Back to details</button></div>
      </div>
    </section>}

    {MANAGED_PAGE_ENABLED && pendingManaged && <section className="managed-assets-form managed-creating" aria-live="polite">
      <div className="managed-creating__inner">
        <span className="eyebrow">CREATING</span>
        <h2>Your Facebook Page is being created</h2>
        <p className="managed-creating__caption">{creationCaption}</p>
        <div className="managed-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)}><i style={{ width: `${Math.max(progress, 4)}%` }} /></div>
        <p className="managed-progress__note">Facebook only lets Pages be created one at a time, so this usually takes a few minutes. You can safely leave: creation finishes in the background and the Page appears under Connected when it is ready.</p>
      </div>
    </section>}

    {MANAGED_PAGE_ENABLED && step === "done" && liveManaged && <section className="managed-assets-form managed-done">
      <div className="managed-done__row"><CheckCircle2 /><div><strong>Your Facebook Page is live</strong><p>{accountName(managedPage as SocialAccount)} is ready to run campaigns.{" "}{managedPage?.url && <a href={managedPage.url} target="_blank" rel="noreferrer">View it on Facebook</a>}</p></div><button className="button button--secondary button--small" type="button" onClick={() => { setStep("closed"); void openEditor(); }}>Edit Page branding</button></div>
    </section>}

    {MANAGED_PAGE_ENABLED && editorOpen && liveManaged && <form className="managed-assets-form managed-editor" noValidate onSubmit={(event) => { event.preventDefault(); void saveEditor(); }}>
      <div className="managed-assets-form__intro"><span className="eyebrow">YOUR FACEBOOK PAGE</span><h2>Edit Page branding</h2><p>This is your Page as it appears on Facebook. Click the cover, the profile picture, or the intro text to change them. Changes are pushed to Facebook automatically and usually appear within a few minutes.</p><div className="managed-name-warning" role="note"><Lock aria-hidden="true" /><p>Facebook doesn’t allow created Pages to be renamed.</p></div></div>
      <div className="managed-assets-form__fields">
        <div className="fb-preview fb-preview--edit">
          <div className="fb-preview__cover">
            {(editorBanner?.preview || editorBranding?.banner_url) && <img src={editorBanner?.preview || editorBranding?.banner_url || ""} alt="" />}
            <label className="fb-edit-chip fb-edit-chip--cover"><input type="file" accept="image/png,image/jpeg,image/gif" onChange={(event) => { const file = event.target.files?.[0]; if (file) { setEditorNotice(""); void pickImage("editor-banner", file); } }} /><Camera aria-hidden="true" /> {busy === "editor-banner" ? "Uploading…" : "Edit cover photo"}</label>
          </div>
          <div className="fb-preview__head">
            <span className="fb-preview__avatar fb-preview__avatar--edit">
              {(editorLogo?.preview || editorBranding?.logo_url) ? <img src={editorLogo?.preview || editorBranding?.logo_url || ""} alt="" /> : <Facebook />}
              <label className="fb-edit-chip fb-edit-chip--avatar" title={busy === "editor-logo" ? "Uploading…" : "Replace profile picture"}><input type="file" accept="image/png,image/jpeg,image/gif" aria-label="Replace profile picture" onChange={(event) => { const file = event.target.files?.[0]; if (file) { setEditorNotice(""); void pickImage("editor-logo", file); } }} />{busy === "editor-logo" ? <LoaderCircle className="spin" aria-hidden="true" /> : <Camera aria-hidden="true" />}</label>
            </span>
            <div className="fb-preview__title"><strong>{editorBranding?.page_name || accountName(managedPage as SocialAccount)}</strong><small>Page</small></div>
            <div className="fb-preview__cta"><span className="fb-preview__like"><ThumbsUp /> Like</span><span><MessageCircle /> Message</span></div>
          </div>
          <div className="fb-preview__tabs"><span className="is-active">Posts</span><span>About</span><span>Photos</span><span>Videos</span><span>More</span></div>
          <div className="fb-preview__about"><strong>Intro</strong><textarea maxLength={500} value={editorDescription} placeholder="Describe your business" aria-label="Page description" onChange={(event) => { setEditorDescription(event.target.value); setEditorNotice(""); }} /></div>
        </div>
        {editorNotice && <p className="managed-editor-notice" role="status">{editorNotice}</p>}
        <div className="managed-assets-form__actions"><button className="button button--primary" type="submit" disabled={Boolean(busy)}>{busy === "editor" ? "Saving…" : "Save changes"}</button><button className="button button--secondary" type="button" onClick={() => setEditorOpen(false)}>Close</button></div>
      </div>
    </form>}

    <section className="connected-assets">
      <div className="section-title"><div><span className="eyebrow">CONNECTED</span><h2>Facebook and Instagram</h2><p>These are the Pages and profiles available for your campaigns.</p></div></div>
      {items === null ? <div className="asset-loading"><LoaderCircle className="spin" /> Loading identities…</div> : items.length === 0 ? <div className="asset-empty"><Facebook /><strong>No identity connected yet</strong><p>Connect an existing account or create a Facebook Page powered by AdRoyalties.</p></div> : <div className="asset-list">{items.map((account) => {
        const picture = account.profile_picture_url || account.picture_url;
        const name = accountName(account);
        const pageUrl = typeof account.url === "string" && /^https:\/\//.test(account.url) ? account.url : null;
        return <article key={account.id || name}>{picture ? <img src={picture} alt="" /> : <span>{account.platform === "instagram" ? <Instagram /> : <Facebook />}</span>}<div><small>{account.platform === "instagram" ? "Instagram profile" : "Facebook Page"}</small>{pageUrl ? <a className="asset-list__name" href={pageUrl} target="_blank" rel="noreferrer">{name} <ExternalLink /></a> : <strong>{name}</strong>}{account.username && !/^\d+$/.test(account.username) && <p>@{account.username}</p>}</div><em><CheckCircle2 />{accountStatus(account)}</em><button type="button" aria-label={`Disconnect ${name}`} onClick={() => void disconnect(account)} disabled={busy === account.id}><Trash2 /></button></article>;
      })}</div>}
    </section>
  </div>;
}

// A lightweight mock of the Facebook Page header so the client can see the
// locked name, picture, and cover in place before anything is created.
function FacebookPreview({ name, about, logoUrl, bannerUrl }: { name: string; about: string; logoUrl: string | null; bannerUrl: string | null }) {
  return <div className="fb-preview" aria-label="Preview of your Facebook Page">
    <div className="fb-preview__cover">{bannerUrl && <img src={bannerUrl} alt="" />}</div>
    <div className="fb-preview__head">
      <span className="fb-preview__avatar">{logoUrl ? <img src={logoUrl} alt="" /> : <Facebook />}</span>
      <div className="fb-preview__title"><strong>{name}</strong><small>Page</small></div>
      <div className="fb-preview__cta"><span className="fb-preview__like"><ThumbsUp /> Like</span><span><MessageCircle /> Message</span></div>
    </div>
    <div className="fb-preview__tabs"><span className="is-active">Posts</span><span>About</span><span>Photos</span><span>Videos</span><span>More</span></div>
    {about && <div className="fb-preview__about"><strong>Intro</strong><p>{about}</p></div>}
  </div>;
}

function ImageField({ label, value, busy, required, invalid, errorId, onFile }: { label: string; value?: string; busy: boolean; required?: boolean; invalid?: boolean; errorId?: string; onFile: (file: File) => void }) {
  return <div className={`asset-upload-field ${invalid ? "has-error" : ""}`}><label className="asset-upload"><input type="file" accept="image/png,image/jpeg,image/gif" aria-required={required} aria-invalid={invalid} aria-describedby={invalid ? errorId : undefined} onChange={(event) => event.target.files?.[0] && onFile(event.target.files[0])} /><ImagePlus /><span><strong>{busy ? "Uploading…" : value || <>{label}{required && <em aria-hidden="true"> *</em>}</>}</strong><small>{value ? "Ready" : "PNG, JPG, or GIF"}</small></span></label>{invalid && <small className="field-error" id={errorId}>Required</small>}</div>;
}
